import i18n, { SUPPORTED_LANGUAGES, type SupportedLanguage } from "../i18n";

/**
 * The UI language a clinician chose, which lives on their ACCOUNT rather than in
 * one browser's localStorage, so it follows them to any machine they sign in on.
 *
 * i18next's localStorage cache is still there and still useful: it is what the
 * sign-in screen reads, since nobody is authenticated yet and there is no
 * account to consult. The account is the authority the moment there is one.
 */

/** The bare language part: the catalogues are language-only (`load: "languageOnly"`). */
export function currentUiLanguage(): SupportedLanguage {
  const base = i18n.language.split("-")[0];
  return isSupportedLanguage(base) ? base : "fr";
}

export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Applies the preference stored on the account, if there is one this build can
 * render.
 *
 * Three no-ops, all deliberate: **null** means the clinician never chose, so the
 * app's own default rule stands rather than being overwritten with a guess; a
 * code this build ships no catalogue for is left alone rather than switching the
 * UI to a fallback the account never asked for; and re-applying the language
 * already active is skipped, because `changeLanguage` notifies every subscriber
 * and would re-render the whole app on each identity call.
 */
export function applyPreferredLanguage(preferred: string | null | undefined) {
  if (!preferred) return;
  const base = preferred.split("-")[0];
  if (!isSupportedLanguage(base)) return;
  if (currentUiLanguage() === base) return;
  // Also writes i18next's localStorage cache, so the next sign-in screen on
  // this machine already speaks the right language.
  void i18n.changeLanguage(base);
}
